import React, { useCallback } from 'react';
import { useIntl } from 'react-intl';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChartLine } from '@fortawesome/pro-light-svg-icons/faChartLine';
import { TypedMemo } from '../../common/types';
import { LoadingView } from '../../common/views/LoadingView';
import { ErrorView } from '../../common/views/ErrorView/ErrorView';
import { Panel, PanelHead } from '../components/primitives/Panel';
import { useSocialPosts } from '../promote/hooks/useSocialPosts';
import { SerializedSocialPost, SocialPlatform } from '../promote/types';
import { SocialPostsEmpty } from './SocialPostsEmpty';
import { SocialPostsFilters } from './SocialPostsFilters';
import { SocialPostsTable } from './SocialPostsTable';
import { useSocialPostsFilters } from './useSocialPostsFilters';
import { socialMessages as messages } from './socialMessages';
/* eslint-disable css-modules/no-unused-class -- one stylesheet per screen; each part of the screen uses its own slice of it */
import styles from './Social.module.scss';
/* eslint-enable css-modules/no-unused-class */

interface SocialPostsPanelProps {
  accountId: number;
  /** The period in days. */
  days: number;
  /** The connected channels, for the channel filter. */
  platforms: SocialPlatform[];
  openPostId?: number;
  onOpen: (post: SerializedSocialPost) => void;
}

/** The block "Posts": the filters, the table and its loading, empty and error states. */
const SocialPostsPanelComponent: React.FC<SocialPostsPanelProps> = ({
  accountId,
  days,
  platforms,
  openPostId,
  onOpen,
}) => {
  const intl = useIntl();
  const { filters, isFiltered, setPlatform, setType, toggleOurs, clear } = useSocialPostsFilters();
  const { posts, hasMore, isLoading, isLoadingMore, error, loadMoreError, retry, loadMore } = useSocialPosts({
    accountId,
    days,
    platform: filters.platform,
    type: filters.type,
    ours: filters.oursOnly ? true : undefined,
  });

  const onLoadMore = useCallback(() => {
    loadMore().catch(console.error);
  }, [loadMore]);

  // The server gives no total, so the count is the posts on screen. A "+" says that more exist.
  const count =
    posts === undefined
      ? null
      : intl.formatMessage(hasMore ? messages.postsCountMore : messages.postsCount, { count: posts.length });

  return (
    <Panel>
      <PanelHead
        icon={<FontAwesomeIcon icon={faChartLine} />}
        title={
          <>
            {intl.formatMessage(messages.postsTitle)}
            {count === null ? null : <span className={styles.postCount}>{count}</span>}
          </>
        }
        right={
          <SocialPostsFilters
            filters={filters}
            platforms={platforms}
            onPlatform={setPlatform}
            onType={setType}
            onToggleOurs={toggleOurs}
          />
        }
      />
      {isLoading ? <LoadingView /> : null}
      {!isLoading && error !== undefined ? <ErrorView error={error} retry={retry} /> : null}
      {!isLoading && error === undefined && posts !== undefined && posts.length === 0 ? (
        <div className={styles.postEmpty}>
          <SocialPostsEmpty kind="noPosts" onAction={isFiltered ? clear : undefined} />
        </div>
      ) : null}
      {!isLoading && error === undefined && posts !== undefined && posts.length > 0 ? (
        <SocialPostsTable
          posts={posts}
          platform={filters.platform}
          openPostId={openPostId}
          hasMore={hasMore}
          isLoadingMore={isLoadingMore}
          loadMoreError={loadMoreError}
          onOpen={onOpen}
          onLoadMore={onLoadMore}
        />
      ) : null}
    </Panel>
  );
};

export const SocialPostsPanel = TypedMemo(SocialPostsPanelComponent);
